import React, { Fragment, useEffect, useState } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Collapse,
  IconButton,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  ExpandMore,
  ExpandLess,
  Delete as DeleteIcon,
  NotInterested as BlockIcon,
  Edit as EditIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import {
  getFlagsWithEntries,
  deleteFlag,
  updateFlag,
} from "../features/flag/flagSlice";
import {
  updateEntry,
  deleteEntry,
  setEntryInvalid,
  setEntryValid,
} from "../features/entry/entrySlice";
import { useAccessControl } from "../context/AccessControlContext";

const Flags = () => {
  const dispatch = useDispatch();
  const { isAdmin } = useAccessControl();
  const { flagsWithEntries, isLoading } = useSelector((state) => state.flag);
  const { isLoading: entryLoading } = useSelector((state) => state.entry);

  const [expandedFlags, setExpandedFlags] = useState(new Set());
  const [expandedEntries, setExpandedEntries] = useState(new Set());
  const [invalidDialog, setInvalidDialog] = useState({
    open: false,
    entry: null,
  });
  const [editEntryDialog, setEditEntryDialog] = useState({
    open: false,
    entry: null,
  });
  const [entryFormData, setEntryFormData] = useState({});
  const [invalidReason, setInvalidReason] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    dispatch(getFlagsWithEntries());
  }, [dispatch]);

  if (!isAdmin) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography color="error">
          Access Denied: Only administrators can access this page.
        </Typography>
      </Box>
    );
  }

  const toggleFlagRow = (flagId) => {
    const newExpanded = new Set(expandedFlags);
    if (newExpanded.has(flagId)) {
      newExpanded.delete(flagId);
    } else {
      newExpanded.add(flagId);
    }
    setExpandedFlags(newExpanded);
  };

  const toggleEntryRow = (entryId) => {
    const newExpanded = new Set(expandedEntries);
    if (newExpanded.has(entryId)) {
      newExpanded.delete(entryId);
    } else {
      newExpanded.add(entryId);
    }
    setExpandedEntries(newExpanded);
  };

  const handleDeleteFlag = async (flagId) => {
    if (window.confirm("Are you sure you want to delete this flag?")) {
      await dispatch(deleteFlag(flagId));
    }
  };

  const handleOpenInvalidDialog = (entry) => {
    setInvalidDialog({ open: true, entry });
    setInvalidReason("");
    setError("");
  };

  const handleCloseInvalidDialog = () => {
    setInvalidDialog({ open: false, entry: null });
    setInvalidReason("");
    setError("");
  };

  const handleMarkInvalid = async () => {
    if (!invalidReason.trim()) {
      setError("Please provide a reason for marking this entry as invalid");
      return;
    }

    const result = await dispatch(
      setEntryInvalid({
        id: invalidDialog.entry.id,
        reason: invalidReason,
        markedInvalidBy: null,
      }),
    );

    if (result.type === setEntryInvalid.fulfilled.type) {
      handleCloseInvalidDialog();
      dispatch(getFlagsWithEntries());
    } else {
      setError("Failed to mark entry as invalid");
    }
  };

  const handleMarkValid = async (entryId) => {
    const result = await dispatch(setEntryValid(entryId));

    if (result.type === setEntryValid.fulfilled.type) {
      dispatch(getFlagsWithEntries());
    }
  };

  const handleOpenEditDialog = (entry) => {
    setEditEntryDialog({ open: true, entry });
    setEntryFormData({
      points: entry.points,
      position: entry.position,
      district: entry.district,
      event: entry.event,
    });
    setError("");
  };

  const handleCloseEditDialog = () => {
    setEditEntryDialog({ open: false, entry: null });
    setEntryFormData({});
    setError("");
  };

  const handleSaveEntry = async () => {
    const result = await dispatch(
      updateEntry({
        entryId: editEntryDialog.entry.id,
        updateData: entryFormData,
      }),
    );

    if (result.type === updateEntry.fulfilled.type) {
      handleCloseEditDialog();
      dispatch(getFlagsWithEntries());
    } else {
      setError("Failed to update entry");
    }
  };

  const handleDeleteEntry = async (entry) => {
    if (window.confirm("Are you sure you want to delete this entry?")) {
      const result = await dispatch(deleteEntry(entry.id));
      if (result.type === deleteEntry.fulfilled.type) {
        dispatch(getFlagsWithEntries());
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB");
  };

  return (
    <Box sx={{ py: 3, px: { xs: 2, sm: 3 } }}>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          sx={{
            mb: 1,
            fontWeight: 700,
            fontSize: { xs: "1.3rem", sm: "1.5rem" },
          }}
        >
          🚩 Flagged Entries
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Review and manage flagged entries from users
        </Typography>
      </Box>

      {isLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{ py: 12 }}
        >
          <CircularProgress size={50} />
        </Box>
      ) : flagsWithEntries.length === 0 ? (
        <Box
          sx={{
            p: 4,
            textAlign: "center",
            backgroundColor: "background.paper",
            borderRadius: 2,
          }}
        >
          <Typography variant="body1" color="text.secondary">
            ✓ No flagged entries at this time
          </Typography>
        </Box>
      ) : (
        <Box sx={{ overflowX: "auto", width: "100%" }}>
          <Table
            sx={{
              minWidth: 700,
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
              "& td, & th": { border: "none" },
            }}
          >
            <TableHead>
              <TableRow sx={{ backgroundColor: "action.hover" }}>
                <TableCell
                  sx={{
                    py: 2,
                    px: 1.5,
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    fontWeight: 700,
                    color: "text.primary",
                  }}
                />
                <TableCell
                  sx={{
                    py: 2,
                    px: 1.5,
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    fontWeight: 700,
                    color: "text.primary",
                  }}
                >
                  Player
                </TableCell>
                <TableCell
                  sx={{
                    py: 2,
                    px: 1.5,
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    fontWeight: 700,
                    color: "text.primary",
                  }}
                >
                  Event
                </TableCell>
                <TableCell
                  sx={{
                    py: 2,
                    px: 1.5,
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    fontWeight: 700,
                    color: "text.primary",
                  }}
                >
                  Date
                </TableCell>
                <TableCell
                  sx={{
                    py: 2,
                    px: 1.5,
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    fontWeight: 700,
                    color: "text.primary",
                  }}
                >
                  Reason
                </TableCell>
                <TableCell
                  sx={{
                    py: 2,
                    px: 1.5,
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    fontWeight: 700,
                    color: "text.primary",
                  }}
                >
                  Status
                </TableCell>
                <TableCell
                  sx={{
                    py: 2,
                    px: 1.5,
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    fontWeight: 700,
                    color: "text.primary",
                  }}
                  align="right"
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {flagsWithEntries.map((flag, index) => {
                const flagKey = `flag-${flag.id}`;
                const isFlagExpanded = expandedFlags.has(flagKey);

                return (
                  <Fragment key={flagKey}>
                    {/* Flag Row */}
                    <TableRow
                      sx={{
                        height: "auto",
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                        transition: "background-color 0.2s",
                      }}
                    >
                      <TableCell
                        sx={{
                          py: 1.25,
                          px: 1.5,
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => toggleFlagRow(flagKey)}
                          sx={{ p: 0.5 }}
                        >
                          {isFlagExpanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.25,
                          px: 1.5,
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                          fontWeight: 500,
                        }}
                      >
                        {flag.player_name}
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.25,
                          px: 1.5,
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        }}
                      >
                        {flag.event_name}
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.25,
                          px: 1.5,
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        }}
                      >
                        {formatDate(flag.event_date)}
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.25,
                          px: 1.5,
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              backgroundColor: "error.main",
                            }}
                          />
                          {flag.reason}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.25,
                          px: 1.5,
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        }}
                      >
                        <Chip
                          label={flag.status || "pending"}
                          color={
                            flag.status === "resolved"
                              ? "success"
                              : flag.status === "reviewed"
                                ? "warning"
                                : "default"
                          }
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.25,
                          px: 1.5,
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        }}
                        align="right"
                      >
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteFlag(flag.id)}
                          title="Delete flag"
                          sx={{
                            "&:hover": { backgroundColor: "error.lighter" },
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>

                    {/* Flag Details and Entries */}
                    <TableRow key={`${flagKey}-details`}>
                      <TableCell
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                        colSpan={7}
                      >
                        <Collapse
                          in={isFlagExpanded}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box
                            sx={{
                              margin: 2,
                              marginTop: 1.5,
                              backgroundColor: "background.paper",
                              borderRadius: 1.5,
                              p: 3,
                              color: "text.primary",
                            }}
                          >
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                  xs: "1fr",
                                  md: "1fr 1fr",
                                },
                                gap: 3,
                                mb: 3,
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    mb: 1.5,
                                    fontWeight: 700,
                                    fontSize: "0.95rem",
                                    color: "primary.main",
                                  }}
                                >
                                  Flag Information
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: {
                                        xs: "0.7rem",
                                        sm: "0.875rem",
                                      },
                                      color: "text.primary",
                                    }}
                                  >
                                    <strong>Reason:</strong> {flag.reason}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: {
                                        xs: "0.7rem",
                                        sm: "0.875rem",
                                      },
                                      color: "text.primary",
                                    }}
                                  >
                                    <strong>Status:</strong>{" "}
                                    <Chip
                                      label={flag.status || "pending"}
                                      color={
                                        flag.status === "resolved"
                                          ? "success"
                                          : flag.status === "reviewed"
                                            ? "warning"
                                            : "default"
                                      }
                                      size="small"
                                      sx={{ ml: 1 }}
                                    />
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: {
                                        xs: "0.7rem",
                                        sm: "0.875rem",
                                      },
                                      color: "text.primary",
                                    }}
                                  >
                                    <strong>Created:</strong>{" "}
                                    {formatDate(flag.created_at)}
                                  </Typography>
                                </Box>
                              </Box>
                              <Box>
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    mb: 1.5,
                                    fontWeight: 700,
                                    fontSize: "0.95rem",
                                    color: "primary.main",
                                  }}
                                >
                                  Metadata
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: {
                                        xs: "0.7rem",
                                        sm: "0.875rem",
                                      },
                                      color: "text.primary",
                                    }}
                                  >
                                    <strong>Flagged By:</strong>{" "}
                                    {flag.user_email ||
                                      flag.user_id ||
                                      "Anonymous"}
                                  </Typography>
                                  {flag.extra_info && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontSize: {
                                          xs: "0.7rem",
                                          sm: "0.875rem",
                                        },
                                        color: "text.primary",
                                      }}
                                    >
                                      <strong>Additional Info:</strong>{" "}
                                      {flag.extra_info}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </Box>

                            {/* Entries Table */}
                            <Box sx={{ mt: 3, pt: 2 }}>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  mb: 2,
                                  fontWeight: 700,
                                  fontSize: "0.95rem",
                                  color: "primary.main",
                                }}
                              >
                                Related Entry
                              </Typography>
                              {flag.entries && flag.entries.length > 0 ? (
                                <Box sx={{ overflowX: "auto" }}>
                                  <Table
                                    size="small"
                                    sx={{
                                      backgroundColor: "background.default",
                                      borderRadius: 1,
                                      "& td, & th": { border: "none" },
                                    }}
                                  >
                                    <TableHead>
                                      <TableRow
                                        sx={{ backgroundColor: "action.hover" }}
                                      >
                                        <TableCell
                                          sx={{
                                            py: 1,
                                            px: 1.5,
                                            fontSize: "0.75rem",
                                            fontWeight: 700,
                                          }}
                                        />
                                        <TableCell
                                          sx={{
                                            py: 1,
                                            px: 1.5,
                                            fontSize: "0.75rem",
                                            fontWeight: 700,
                                          }}
                                        >
                                          Event
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            py: 1,
                                            px: 1.5,
                                            fontSize: "0.75rem",
                                            fontWeight: 700,
                                          }}
                                        >
                                          Date
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            py: 1,
                                            px: 1.5,
                                            fontSize: "0.75rem",
                                            fontWeight: 700,
                                          }}
                                        >
                                          Position
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            py: 1,
                                            px: 1.5,
                                            fontSize: "0.75rem",
                                            fontWeight: 700,
                                          }}
                                        >
                                          District
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            py: 1,
                                            px: 1.5,
                                            fontSize: "0.75rem",
                                            fontWeight: 700,
                                          }}
                                        >
                                          Points
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            py: 1,
                                            px: 1.5,
                                            fontSize: "0.75rem",
                                            fontWeight: 700,
                                          }}
                                          align="right"
                                        >
                                          Actions
                                        </TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {flag.entries.map((entry) => {
                                        const entryKey = `entry-${entry.id}`;
                                        const isEntryExpanded =
                                          expandedEntries.has(entryKey);

                                        return (
                                          <Fragment key={entryKey}>
                                            <TableRow
                                              sx={{
                                                "&:hover": {
                                                  backgroundColor:
                                                    "action.hover",
                                                },
                                              }}
                                            >
                                              <TableCell
                                                sx={{ py: 0.75, px: 1.5 }}
                                              >
                                                <IconButton
                                                  size="small"
                                                  onClick={() =>
                                                    toggleEntryRow(entryKey)
                                                  }
                                                  sx={{ p: 0.25 }}
                                                >
                                                  {isEntryExpanded ? (
                                                    <ExpandLess />
                                                  ) : (
                                                    <ExpandMore />
                                                  )}
                                                </IconButton>
                                              </TableCell>
                                              <TableCell
                                                sx={{
                                                  py: 0.75,
                                                  px: 1.5,
                                                  fontSize: "0.75rem",
                                                  fontWeight: 500,
                                                }}
                                              >
                                                {entry.event}
                                              </TableCell>
                                              <TableCell
                                                sx={{
                                                  py: 0.75,
                                                  px: 1.5,
                                                  fontSize: "0.75rem",
                                                }}
                                              >
                                                {formatDate(entry.event_date)}
                                              </TableCell>
                                              <TableCell
                                                sx={{
                                                  py: 0.75,
                                                  px: 1.5,
                                                  fontSize: "0.75rem",
                                                }}
                                              >
                                                {entry.position_name ||
                                                  entry.position ||
                                                  "—"}
                                              </TableCell>
                                              <TableCell
                                                sx={{
                                                  py: 0.75,
                                                  px: 1.5,
                                                  fontSize: "0.75rem",
                                                }}
                                              >
                                                {entry.district || "—"}
                                              </TableCell>
                                              <TableCell
                                                sx={{
                                                  py: 0.75,
                                                  px: 1.5,
                                                  fontSize: "0.75rem",
                                                  fontWeight: 600,
                                                }}
                                              >
                                                {entry.points || 0}
                                              </TableCell>
                                              <TableCell
                                                sx={{ py: 0.75, px: 1.5 }}
                                                align="right"
                                              >
                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    gap: 0.5,
                                                    justifyContent: "flex-end",
                                                  }}
                                                >
                                                  <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() =>
                                                      handleOpenEditDialog(
                                                        entry,
                                                      )
                                                    }
                                                    title="Edit entry"
                                                    sx={{
                                                      "&:hover": {
                                                        backgroundColor:
                                                          "primary.lighter",
                                                      },
                                                    }}
                                                  >
                                                    <EditIcon
                                                      sx={{ fontSize: 16 }}
                                                    />
                                                  </IconButton>
                                                  {entry.is_valid === null ? (
                                                    <Fragment>
                                                      <IconButton
                                                        size="small"
                                                        color="success"
                                                        onClick={() =>
                                                          handleMarkValid(
                                                            entry.id,
                                                          )
                                                        }
                                                        title="Mark as valid"
                                                        sx={{
                                                          "&:hover": {
                                                            backgroundColor:
                                                              "success.lighter",
                                                          },
                                                        }}
                                                      >
                                                        <CheckIcon
                                                          sx={{ fontSize: 16 }}
                                                        />
                                                      </IconButton>
                                                      <IconButton
                                                        size="small"
                                                        color="warning"
                                                        onClick={() =>
                                                          handleOpenInvalidDialog(
                                                            entry,
                                                          )
                                                        }
                                                        title="Mark as invalid"
                                                        sx={{
                                                          "&:hover": {
                                                            backgroundColor:
                                                              "warning.lighter",
                                                          },
                                                        }}
                                                      >
                                                        <BlockIcon
                                                          sx={{ fontSize: 16 }}
                                                        />
                                                      </IconButton>
                                                    </Fragment>
                                                  ) : entry.is_valid ===
                                                    false ? (
                                                    <IconButton
                                                      size="small"
                                                      color="success"
                                                      onClick={() =>
                                                        handleMarkValid(
                                                          entry.id,
                                                        )
                                                      }
                                                      title="Mark as valid"
                                                      sx={{
                                                        "&:hover": {
                                                          backgroundColor:
                                                            "success.lighter",
                                                        },
                                                      }}
                                                    >
                                                      <CheckIcon
                                                        sx={{ fontSize: 16 }}
                                                      />
                                                    </IconButton>
                                                  ) : (
                                                    <IconButton
                                                      size="small"
                                                      color="warning"
                                                      onClick={() =>
                                                        handleOpenInvalidDialog(
                                                          entry,
                                                        )
                                                      }
                                                      title="Mark as invalid"
                                                      sx={{
                                                        "&:hover": {
                                                          backgroundColor:
                                                            "warning.lighter",
                                                        },
                                                      }}
                                                    >
                                                      <BlockIcon
                                                        sx={{ fontSize: 16 }}
                                                      />
                                                    </IconButton>
                                                  )}
                                                  <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() =>
                                                      handleDeleteEntry(entry)
                                                    }
                                                    title="Delete entry"
                                                    sx={{
                                                      "&:hover": {
                                                        backgroundColor:
                                                          "error.lighter",
                                                      },
                                                    }}
                                                  >
                                                    <DeleteIcon
                                                      sx={{ fontSize: 16 }}
                                                    />
                                                  </IconButton>
                                                </Box>
                                              </TableCell>
                                            </TableRow>

                                            {/* Entry Details */}
                                            <TableRow
                                              key={`${entryKey}-details`}
                                            >
                                              <TableCell
                                                colSpan={7}
                                                sx={{ py: 0, px: 0 }}
                                              >
                                                <Collapse
                                                  in={isEntryExpanded}
                                                  timeout="auto"
                                                  unmountOnExit
                                                >
                                                  <Box
                                                    sx={{
                                                      margin: 1,
                                                      p: 1.5,
                                                      backgroundColor:
                                                        "action.hover",
                                                      borderRadius: 1,
                                                      color: "text.primary",
                                                    }}
                                                  >
                                                    <Box
                                                      sx={{
                                                        display: "grid",
                                                        gridTemplateColumns: {
                                                          xs: "1fr",
                                                          sm: "1fr 1fr",
                                                        },
                                                        gap: 2,
                                                      }}
                                                    >
                                                      <Typography
                                                        variant="caption"
                                                        sx={{
                                                          fontSize: "0.75rem",
                                                          color: "text.primary",
                                                        }}
                                                      >
                                                        <strong>ID:</strong>{" "}
                                                        {entry.id}
                                                      </Typography>
                                                      <Typography
                                                        variant="caption"
                                                        sx={{
                                                          fontSize: "0.75rem",
                                                          color: "text.primary",
                                                        }}
                                                      >
                                                        <strong>
                                                          Category:
                                                        </strong>{" "}
                                                        {entry.category_name ||
                                                          entry.category ||
                                                          "—"}
                                                      </Typography>
                                                      <Typography
                                                        variant="caption"
                                                        sx={{
                                                          fontSize: "0.75rem",
                                                          color: "text.primary",
                                                        }}
                                                      >
                                                        <strong>Format:</strong>{" "}
                                                        {entry.format_name ||
                                                          entry.format ||
                                                          "—"}
                                                      </Typography>
                                                      <Typography
                                                        variant="caption"
                                                        sx={{
                                                          fontSize: "0.75rem",
                                                          color: "text.primary",
                                                        }}
                                                      >
                                                        <strong>Level:</strong>{" "}
                                                        {entry.level_name ||
                                                          entry.level ||
                                                          "—"}
                                                      </Typography>
                                                    </Box>
                                                    {!entry.is_valid && (
                                                      <Chip
                                                        label={`Invalid: ${entry.invalid_reason}`}
                                                        color="error"
                                                        size="small"
                                                        sx={{ mt: 1.5 }}
                                                        variant="outlined"
                                                      />
                                                    )}
                                                  </Box>
                                                </Collapse>
                                              </TableCell>
                                            </TableRow>
                                          </Fragment>
                                        );
                                      })}
                                    </TableBody>
                                  </Table>
                                </Box>
                              ) : (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontSize: "0.75rem" }}
                                >
                                  No matching entries found for this flag.
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Mark as Invalid Dialog */}
      <Dialog
        open={invalidDialog.open}
        onClose={handleCloseInvalidDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Mark Entry as Invalid</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to mark this entry as invalid? Please provide
            a reason.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason for marking invalid"
            value={invalidReason}
            onChange={(e) => setInvalidReason(e.target.value)}
            placeholder="Enter the reason for marking this entry as invalid..."
            error={!!error}
            helperText={error}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInvalidDialog}>Cancel</Button>
          <Button
            onClick={handleMarkInvalid}
            variant="contained"
            color="warning"
            disabled={entryLoading}
          >
            {entryLoading ? "Marking..." : "Mark Invalid"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Entry Dialog */}
      <Dialog
        open={editEntryDialog.open}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Entry</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              fullWidth
              label="Event"
              value={entryFormData.event || ""}
              onChange={(e) =>
                setEntryFormData({ ...entryFormData, event: e.target.value })
              }
            />
            <TextField
              fullWidth
              label="Points"
              type="number"
              value={entryFormData.points || 0}
              onChange={(e) =>
                setEntryFormData({
                  ...entryFormData,
                  points: parseFloat(e.target.value),
                })
              }
            />
            <TextField
              fullWidth
              label="Position"
              value={entryFormData.position || ""}
              onChange={(e) =>
                setEntryFormData({ ...entryFormData, position: e.target.value })
              }
            />
            <TextField
              fullWidth
              label="District"
              value={entryFormData.district || ""}
              onChange={(e) =>
                setEntryFormData({ ...entryFormData, district: e.target.value })
              }
            />
            {error && (
              <Typography variant="caption" color="error">
                {error}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button
            onClick={handleSaveEntry}
            variant="contained"
            color="primary"
            disabled={entryLoading}
          >
            {entryLoading ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Flags;
